import { useState, useEffect } from 'react';

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  state: string | null;
  isLoading: boolean;
  error: string | null;
  permissionDenied: boolean;
}

const LOCATION_CACHE_KEY = 'cat_adoption_location';
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

interface CachedLocation {
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  timestamp: number;
}

export function useGeolocation() {
  const [location, setLocation] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    city: null,
    state: null,
    isLoading: true,
    error: null,
    permissionDenied: false,
  });

  const fetchLocation = async () => {
    // Check cache first
    try {
      const cached = localStorage.getItem(LOCATION_CACHE_KEY);
      if (cached) {
        const parsedCache: CachedLocation = JSON.parse(cached);
        const now = Date.now();
        
        // Use cached location if less than 1 hour old
        if (now - parsedCache.timestamp < CACHE_DURATION) {
          setLocation({
            latitude: parsedCache.latitude,
            longitude: parsedCache.longitude,
            city: parsedCache.city,
            state: parsedCache.state,
            isLoading: false,
            error: null,
            permissionDenied: false,
          });
          return;
        }
      }
    } catch (e) {
      // Cache read failed, continue with fresh fetch
    }

    // Request fresh location
    if (!('geolocation' in navigator)) {
      setLocation({
        latitude: null,
        longitude: null,
        city: null,
        state: null,
        isLoading: false,
        error: 'Geolocation is not supported by your browser',
        permissionDenied: false,
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Reverse geocode to get city and state
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          
          const city = data.address?.city || data.address?.town || data.address?.village || 'Your Area';
          const state = data.address?.state || '';

          const locationData = {
            latitude,
            longitude,
            city,
            state,
            isLoading: false,
            error: null,
            permissionDenied: false,
          };

          setLocation(locationData);

          // Cache the location
          try {
            localStorage.setItem(
              LOCATION_CACHE_KEY,
              JSON.stringify({
                latitude,
                longitude,
                city,
                state,
                timestamp: Date.now(),
              })
            );
          } catch (e) {
            // Cache write failed, continue anyway
          }
        } catch (error) {
          setLocation({
            latitude,
            longitude,
            city: 'Your Area',
            state: '',
            isLoading: false,
            error: null,
            permissionDenied: false,
          });
        }
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        let permissionDenied = false;

        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Location permission denied. Please enable location access to see cats near you.';
          permissionDenied = true;
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'Location information is unavailable';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Location request timed out';
        }

        setLocation({
          latitude: null,
          longitude: null,
          city: null,
          state: null,
          isLoading: false,
          error: errorMessage,
          permissionDenied,
        });
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: CACHE_DURATION,
      }
    );
  };

  useEffect(() => {
    fetchLocation();
  }, []);

  const refetch = () => {
    setLocation((prev) => ({ ...prev, isLoading: true, error: null }));
    fetchLocation();
  };

  const clearCache = () => {
    try {
      localStorage.removeItem(LOCATION_CACHE_KEY);
    } catch (e) {
      // Ignore cache clear errors
    }
  };

  return {
    ...location,
    refetch,
    clearCache,
  };
}